import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { markFriendSent } from "../slices/shareModalSlice";
import { useMessages } from "../../../features/chat/context/MessageProvider";

/**
 * Hook for sending a shared post to a friend via DM chat.
 * Returns { handleSendToFriend, sendingToUser }.
 */
export const useSendToFriend = ({
  postId,
  postData,
  commentary,
  openDirectConversationWithUser,
  usersCache,
}) => {
  const dispatch = useDispatch();
  const { sendMessage } = useMessages();
  const [sendingToUser, setSendingToUser] = useState(null);

  const getFriendName = useCallback((friendId) => {
    const user = usersCache[friendId];
    return user?.displayName || user?.username || user?.name || 'User';
  }, [usersCache]);

  const handleSendToFriend = useCallback(async (friendId) => {
    if (sendingToUser === friendId) return;
    setSendingToUser(friendId);
    try {
      const conversationId = await openDirectConversationWithUser(friendId);
      if (!conversationId) throw new Error("Could not open conversation");

      const isReshare = postData?.type === 'shared_post' && postData?.sharedPostId;
      const chatSharedPost = {
        id: postId,
        url: `/post/${postId}`,
        title: postData?.title || "",
        content: postData?.content || "",
        imageUrl: postData?.imageUrl || (postData?.imageUrls && postData.imageUrls[0]) || "",
        authorName: postData?.username || "",
        authorPhotoURL: postData?.userPhotoURL || "",
        type: postData?.type || "post",
        userId: postData?.userId,
        username: postData?.username,
      };
      if (isReshare) {
        chatSharedPost.sharedPostId = postData.sharedPostId;
        chatSharedPost.sharedPostData = postData.sharedPostData;
      }

      await sendMessage({
        conversationId,
        content: commentary || "",
        sharedPost: chatSharedPost,
      });
      dispatch(markFriendSent(friendId));
      toast.success("Post sent to chat!");
    } catch (err) {
      console.error("[ShareModal] handleSendToFriend failed", err);
      const friendName = getFriendName(friendId);
      toast.error(`Failed to send to ${friendName}. Try again.`);
    } finally {
      setSendingToUser(null);
    }
  }, [sendingToUser, postData, postId, commentary, dispatch, openDirectConversationWithUser, sendMessage, getFriendName]);

  return { handleSendToFriend, sendingToUser };
};
